import { z } from "zod";
import { PlatformRoleSchema } from "../auth/auth.types";
import {
	AddressSchema,
	OrganizationRoleSchema,
	OrganizationUserInfoSchema,
} from "../organization/organization.types";
import {
	BaseEntitySchema,
	type OrderByParams,
	StatusSchema,
} from "../shared/shared-types";

/**
 * User Service Types
 *
 * Schemas and types for the User service layer
 * Based on Fawkes API /users endpoints
 */

// ============================================================================
// PASSWORD VALIDATION (GET /users/{userId}/validate-password)
// ============================================================================

export type ValidatePasswordResult = {
	isValid: boolean;
	error?: string;
	/** Raw codes from API `errors` (empty when valid) */
	errors: string[];
};

// ============================================================================
// CORE USER SCHEMAS
// ============================================================================

//! User Status
// Single source of truth for user status mappings
export const USER_STATUSES = [
	{ code: 1, name: "Active" },
	{ code: 2, name: "Suspended" },
	{ code: 3, name: "Archived" },
	{ code: 4, name: "Retired" },
	{ code: 5, name: "Pending" },
] as const;

// Legacy enum for backwards compatibility (prefer using codes directly)
export enum USER_STATUS {
	ACTIVE = 1,
	SUSPENDED = 2,
	ARCHIVED = 3,
	RETIRED = 4,
	PENDING = 5,
}

// Type-safe code and name types derived from source of truth
export type UserStatusCode = (typeof USER_STATUSES)[number]["code"]; // 1 | 2 | 3 | 4 | 5
export type UserStatusName = (typeof USER_STATUSES)[number]["name"]; // "Active" | "Suspended" | etc.

// Lookup maps for O(1) conversions
export const USER_STATUS_BY_CODE = Object.fromEntries(
	USER_STATUSES.map((s) => [s.code, s]),
) as Record<UserStatusCode, (typeof USER_STATUSES)[number]>;

export const USER_STATUS_BY_NAME = Object.fromEntries(
	USER_STATUSES.map((s) => [s.name, s]),
) as Record<UserStatusName, (typeof USER_STATUSES)[number]>;

// Helper functions for type-safe conversions
export const getUserStatusName = (code: UserStatusCode): UserStatusName => {
	return USER_STATUS_BY_CODE[code].name;
};

export const getUserStatusCode = (name: UserStatusName): UserStatusCode => {
	return USER_STATUS_BY_NAME[name].code;
};

export const isValidUserStatusCode = (
	code: unknown,
): code is UserStatusCode => {
	return typeof code === "number" && code in USER_STATUS_BY_CODE;
};

export const isValidUserStatusName = (
	name: unknown,
): name is UserStatusName => {
	return typeof name === "string" && name in USER_STATUS_BY_NAME;
};

//! Organization User Status
// Single source of truth for organization user status mappings
export const ORGANIZATION_USER_STATUSES = [
	{ code: 1, name: "Active" },
	{ code: 2, name: "Suspended" },
	{ code: 3, name: "Archived" },
	{ code: 4, name: "Retired" },
	{ code: 5, name: "Pending" },
] as const;

// Legacy enum for backwards compatibility (prefer using codes directly)
export enum ORGANIZATION_USER_STATUS {
	ACTIVE = 1,
	SUSPENDED = 2,
	ARCHIVED = 3,
	RETIRED = 4,
	PENDING = 5,
}

// Type-safe code and name types derived from source of truth
export type OrganizationUserStatusCode =
	(typeof ORGANIZATION_USER_STATUSES)[number]["code"];
export type OrganizationUserStatusName =
	(typeof ORGANIZATION_USER_STATUSES)[number]["name"];

// Lookup maps for O(1) conversions
export const ORGANIZATION_USER_STATUS_BY_CODE = Object.fromEntries(
	ORGANIZATION_USER_STATUSES.map((s) => [s.code, s]),
) as Record<
	OrganizationUserStatusCode,
	(typeof ORGANIZATION_USER_STATUSES)[number]
>;

export const ORGANIZATION_USER_STATUS_BY_NAME = Object.fromEntries(
	ORGANIZATION_USER_STATUSES.map((s) => [s.name, s]),
) as Record<
	OrganizationUserStatusName,
	(typeof ORGANIZATION_USER_STATUSES)[number]
>;

// Helper functions for type-safe conversions
export const getOrganizationUserStatusName = (
	code: OrganizationUserStatusCode,
): OrganizationUserStatusName => {
	return ORGANIZATION_USER_STATUS_BY_CODE[code].name;
};

export const getOrganizationUserStatusCode = (
	name: OrganizationUserStatusName,
): OrganizationUserStatusCode => {
	return ORGANIZATION_USER_STATUS_BY_NAME[name].code;
};

export const isValidOrganizationUserStatusCode = (
	code: unknown,
): code is OrganizationUserStatusCode => {
	return typeof code === "number" && code in ORGANIZATION_USER_STATUS_BY_CODE;
};

export const isValidOrganizationUserStatusName = (
	name: unknown,
): name is OrganizationUserStatusName => {
	return typeof name === "string" && name in ORGANIZATION_USER_STATUS_BY_NAME;
};

//! User Invite Status
// Single source of truth for user invite status mappings
export const USER_INVITE_STATUSES = [
	{ code: 1, name: "Invited" }, // Note: Backend enum is PENDING, but we display as "Invited"
	{ code: 2, name: "Accepted" }, // (kwt 6/24/26 changed from "Active" to "Accepted" per Ted feedback)
	{ code: 3, name: "Revoked" },
	{ code: 4, name: "Expired" },
] as const;

// Legacy enum for backwards compatibility (prefer using codes directly)
export enum USER_INVITE_STATUS {
	PENDING = 1, // Note: Named PENDING in backend, but displays as "Invited"
	ACCEPTED = 2,
	REVOKED = 3,
	EXPIRED = 4,
}

// Type-safe code and name types derived from source of truth
export type UserInviteStatusCode =
	(typeof USER_INVITE_STATUSES)[number]["code"];
export type UserInviteStatusName =
	(typeof USER_INVITE_STATUSES)[number]["name"];

// Lookup maps for O(1) conversions
export const USER_INVITE_STATUS_BY_CODE = Object.fromEntries(
	USER_INVITE_STATUSES.map((s) => [s.code, s]),
) as Record<UserInviteStatusCode, (typeof USER_INVITE_STATUSES)[number]>;

export const USER_INVITE_STATUS_BY_NAME = Object.fromEntries(
	USER_INVITE_STATUSES.map((s) => [s.name, s]),
) as Record<UserInviteStatusName, (typeof USER_INVITE_STATUSES)[number]>;

// Helper functions for type-safe conversions
export const getUserInviteStatusName = (
	code: UserInviteStatusCode,
): UserInviteStatusName => {
	return USER_INVITE_STATUS_BY_CODE[code].name;
};

export const getUserInviteStatusCode = (
	name: UserInviteStatusName,
): UserInviteStatusCode => {
	return USER_INVITE_STATUS_BY_NAME[name].code;
};

export const isValidUserInviteStatusCode = (
	code: unknown,
): code is UserInviteStatusCode => {
	return typeof code === "number" && code in USER_INVITE_STATUS_BY_CODE;
};

export const isValidUserInviteStatusName = (
	name: unknown,
): name is UserInviteStatusName => {
	return typeof name === "string" && name in USER_INVITE_STATUS_BY_NAME;
};

// Legacy function for backwards compatibility (prefer getUserInviteStatusName)
/** @deprecated Use getUserInviteStatusName instead for type safety */
export const getInviteStatusName = (statusCode: number): string => {
	if (isValidUserInviteStatusCode(statusCode)) {
		return getUserInviteStatusName(statusCode);
	}
	return "Unknown";
};

/**
 * Full User schema including all properties
 * Note: Using z.string() instead of z.string().datetime() for timestamp fields
 * to handle the API's microsecond precision format
 */
export const UserSchema = BaseEntitySchema.extend({
	email: z.string().email(),
	status: StatusSchema,
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	middleName: z.string().nullable(),
	displayName: z.string().nullable(),
	statusCode: z.number().int().nullish(), // Not always returned by API
	lastActiveOrganizationId: z.string().uuid().nullish(), // Not always returned by API
	// Changed from .datetime() to .string() to handle microsecond precision
	lastLoggedInAt: z.string().nullable(),
	lastLoggedInAtOffset: z.string().nullable(),
	platformRoles: z.array(PlatformRoleSchema).default([]),
	organizationUserInfo: z.array(OrganizationUserInfoSchema).default([]),

	// Lightweight relations exposed by /users endpoints (not always included)
	organization: z
		.object({
			id: z.string().uuid(),
			name: z.string().nullable(),
		})
		.optional(),
	subOrganization: z
		.object({
			id: z.string().uuid().nullable().optional(),
			name: z.string().nullable(),
			address: AddressSchema.nullable(),
		})
		.optional(),
	subContractor: z
		.object({
			id: z.string().uuid().nullable().optional(),
			name: z.string().nullable(),
			address: AddressSchema.nullable().optional(),
		})
		.optional(),

	// Permission-related fields (from GET /users/{userId}/permissions)
	canManage: z.boolean().optional(),
	permissions: z.array(z.string()).optional(),

	// API bug: status fields are duplicated at root level
	code: z.number().int().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	belongsTo: z.string().nullable(),
	// Added per swagger2.json diff - phone property for user
	// Phone may not always be present at root level (can be in organizationUserInfo)
	phone: z.string().nullable().optional(),
	hasAvatar: z.boolean().optional(),
	/** Display name supplied directly by the backend — no secondary lookup needed */
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
}).passthrough(); // Allow extra fields that may be returned by API (e.g., platformRole, organizations, organizationRoles)

export type User = z.infer<typeof UserSchema>;

/**
 * Schema for organization users in list responses
 * Note: Does not include base entity fields (id, createdBy, etc.) as API doesn't return them
 */
export const OrgUsersSchema = z.object({
	displayName: z.string().nullable(),
	title: z.string().nullable(),
	phone: z.string().nullable(),
	email: z.string().email().nullable(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	middleName: z.string().nullable(),
	status: StatusSchema,
	organizationRoles: z.array(OrganizationRoleSchema).nullable(),
});

export type OrgUsers = z.infer<typeof OrgUsersSchema>;

/**
 * Full organization user schema with base entity fields
 * Use this for detailed user endpoints that return complete entity data
 */
export const OrgUserDetailSchema = BaseEntitySchema.extend({
	displayName: z.string().nullable(),
	title: z.string().nullable(),
	phone: z.string().nullable(),
	email: z.string().email(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	middleName: z.string().nullable(),
	statusCode: z.number().int().nullish(), // Not always returned by API
	status: StatusSchema,
	organizationRoles: z.array(OrganizationRoleSchema).default([]),
});

export type OrgUserDetail = z.infer<typeof OrgUserDetailSchema>;
/**
 * Update User Info request body
 * Used by PUT /users/{userId}
 * Supports partial updates - all fields are optional
 */
export const UpdateUserInfoSchema = z.object({
	firstName: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
	displayName: z.string().nullable().optional(),
	systemRoleId: z.number().optional(),
	// Added per swagger2.json diff - phone property for PUT /users/{userId}
	phone: z.string().nullable().optional(),
});

export type UpdateUserInfo = z.infer<typeof UpdateUserInfoSchema>;

/**
 * Update User Status request body
 * Used by PUT /users/{userId}/status
 */
export const UpdateUserStatusSchema = z.object({
	status: z.string(),
});

export type UpdateUserStatus = z.infer<typeof UpdateUserStatusSchema>;

/**
 * Update User Platform Role request body
 * Used by PUT /users/{userId}/platform-role
 * Maps to C# UpdateUserRoles model
 */
export const UpdateUserPlatformRoleSchema = z.object({
	systemRoleId: z.number(),
});

export type UpdateUserPlatformRole = z.infer<
	typeof UpdateUserPlatformRoleSchema
>;

// ============================================================================
// QUERY PARAMETER TYPES
// ============================================================================

/**
 * Query parameters for fetching users
 * Supports filtering by user ID, search term, status, platform roles, organization memberships, and ordering
 */
export type GetUsersParams = OrderByParams & {
	/** Filter by specific user ID */
	UserId?: string;
	/** Search term to filter users by name or email */
	SearchTerm?: string;
	/** Filter by exact email address */
	Email?: string;
	/** Filter by user status code */
	Status?: number;
	/** Filter by user status codes */
	Statuses?: string;
	/** Include only users with platform roles */
	HasPlatformRoles?: boolean;
	/** Include only users with organization memberships */
	HasOrganizationUsers?: boolean;
	/** Filter by organization ID */
	OrganizationId?: string;
	/** Filter by sub-organization ID */
	SubOrganizationId?: string;
	/** Filter by sub-contractor ID */
	SubContractorId?: string;
	/** Filter by organization role IDs */
	OrganizationRoleIds?: string[];
	/** Filter by platform role IDs */
	PlatformRoleIds?: number[];
	/** Filter users created before this date */
	CreatedBefore?: string;
	/** Filter users created after this date */
	CreatedAfter?: string;
	/** Filter users updated before this date */
	UpdatedBefore?: string;
	/** Filter users updated after this date */
	UpdatedAfter?: string;
	/** Filter users who last logged in before this date */
	LastLoggedInBefore?: string;
	/** Filter users who last logged in after this date */
	LastLoggedInAfter?: string;
	/** Order by fields (supports dot-notation for nested properties) */
	OrderBy?: {
		Fields?: string[];
		Descending?: boolean;
	};
	/** Comma-separated list of columns to include in export */
	columns?: string;
};

/**
 * Query parameters for getting user count
 * Supports the same filters as GetUsersParams
 */
export type GetUserCountParams = GetUsersParams;

// ============================================================================
// RESPONSE TYPES
// ============================================================================

/**
 * Response type for getUserById
 */
export type GetUserByIdResponse = User;

/**
 * Response type for getUsers (paginated)
 */
export type GetUsersResponse = User[];

/**
 * Response type for getUserCount
 */
export type GetUserCountResponse = number;

/**
 * Response type for getUserPermissions
 */
export type GetUserPermissionsResponse = User;

// ============================================================================
// PLATFORM USER CREATION TYPES (STUB FOR CREATE FLOW)
// ============================================================================

export const CreatePlatformUserInfoSchema = z.object({
	firstName: z.string().min(1),
	lastName: z.string().min(1),
	email: z.string().email(),
	roleId: z.coerce.number().int(),
	// Added per swagger2.json diff - phone property for POST /users
	phone: z.string().optional(),
});

export type CreatePlatformUserInfo = z.infer<
	typeof CreatePlatformUserInfoSchema
>;

/**
 * Lightweight role option for the platform role select
 * Matches SelectItemType shape used by SelectField
 */
export const PlatformRoleOptionSchema = z.object({
	id: z.string(),
	label: z.string(),
	supportingText: z.string().optional(),
});

export type PlatformRoleOption = z.infer<typeof PlatformRoleOptionSchema>;

// ============================================================================
// USER STATUSES
// ============================================================================

/**
 * Schema for user status from /users/statuses endpoint
 */
export const UserStatusItemSchema = z.object({
	code: z.number().int(),
	name: z.string().nullable(),
	description: z.string().nullable(),
});

export const UserStatusesArraySchema = z.array(UserStatusItemSchema);

export type UserStatusItem = z.infer<typeof UserStatusItemSchema>;
export type GetUserStatusesResponse = UserStatusItem[];

// ============================================================================
// PASSWORD RESET TYPES
// ============================================================================

/**
 * Reset password request body
 * Used by PUT /users/{userId}/reset-password
 * Based on swagger2.json ResetPasswordRequest schema
 */
export const ResetPasswordRequestSchema = z.object({
	resetPasswordToken: z.string().nullable(),
	newPassword: z.string().nullable(),
});

export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

// ============================================================================
// MFA TYPES
// ============================================================================

/**
 * Request body for PUT /users/{userId}/mfa
 * Based on swagger2.json UserMfaInfo schema
 */
export const UserMfaInfoSchema = z.object({
	phone: z.string().nullable().optional(),
	verificationCode: z.string().nullable().optional(),
});

export type UserMfaInfo = z.infer<typeof UserMfaInfoSchema>;
